// frontend/src/pages/canvasser/TodayPage.tsx
import { useEffect } from 'react'
import { Spinner, Text, VStack } from '@chakra-ui/react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useToday } from '../../hooks/useToday'
import { isCacheExpiredError } from '../../lib/offlineStore'
import { openableCards, type TodayCard } from '../../lib/todayAssignments'
import { clearChosenAssignment, rememberChosenAssignment } from '../../lib/walkPrefs'
import { Card } from '../../components/shared/Card'
import { CacheExpiredNotice } from '../../components/canvasser/CacheExpiredNotice'

/**
 * [CF-1 P3 §7.1] `/canvasser/today` (and inline at `/canvasser` with 2+ assignments and no choice):
 * one card per assignment. Opening it clears the remembered choice; a tap remembers one and goes
 * home, which opens it. With one assignment there is no picker.
 */
export function TodayPage() {
  const navigate = useNavigate()
  const campaign = useGrantCampaign()
  const campaignId = campaign?.id
  const { cards: allCards, isLoading, error } = useToday(campaignId)
  const cards = openableCards(allCards)

  useEffect(() => { if (campaignId) clearChosenAssignment(campaignId) }, [campaignId])

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (error && isCacheExpiredError(error)) return <CacheExpiredNotice />
  if (!campaignId || cards.length <= 1) return <Navigate to="/canvasser" replace />

  const open = (card: TodayCard) => {
    rememberChosenAssignment(campaignId, card.key)
    navigate('/canvasser')
  }

  return (
    <VStack align="stretch" gap={3} px={4} py={4} pb="calc(16px + env(safe-area-inset-bottom, 0px))">
      <Text as="h1" fontWeight="bold" fontSize="xl">Today</Text>
      {cards.map(card => (
        <Card key={card.key} asChild w="100%" minH="48px" p={4} textAlign="left" cursor="pointer">
          <button type="button" onClick={() => open(card)}>
            <Text as="span" display="block" fontWeight="semibold">{card.label}</Text>
            <Text as="span" display="block" fontSize="sm" color="fg.muted">
              {`${card.total} ${card.unit} · ${card.done === 0 ? 'not started' : `${card.done} done`}`}
            </Text>
          </button>
        </Card>
      ))}
    </VStack>
  )
}
